import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './shared/Card';
import { Badge } from './shared/Badge';
import { ArrowRightIcon, CheckCircleIcon, LockIcon } from 'lucide-react';
type AssessmentsHubProps = {
  onComplete: () => void;
};
export const AssessmentsHub: React.FC<AssessmentsHubProps> = ({
  onComplete
}) => {
  const [xp, setXp] = useState(0);
  const [completedAssessments, setCompletedAssessments] = useState<string[]>([]);
  const assessments = [{
    id: 'mbti',
    title: 'MBTI Personality',
    description: 'Discover how your personality traits align with different career paths',
    xp: 150,
    time: '10 min',
    icon: '🧠',
    badge: 'Self Explorer'
  }, {
    id: 'dmit',
    title: 'DMIT Assessment',
    description: 'Analyze your dermatoglyphics for innate talents and aptitudes',
    xp: 200,
    time: '15 min',
    icon: '👆',
    badge: 'Pattern Analyst'
  }, {
    id: 'astro',
    title: 'Astrological Chart',
    description: 'See how your birth chart influences your professional strengths',
    xp: 250,
    time: '12 min',
    icon: '✨',
    badge: 'Cosmic Navigator'
  }, {
    id: 'interest',
    title: 'Career Interest Inventory',
    description: 'Identify your strongest vocational interests and preferences',
    xp: 175,
    time: '8 min',
    icon: '📊',
    badge: 'Interest Explorer'
  }];
  const startAssessment = (id: string) => {
    // In a real app, this would navigate to the assessment
    // For demo purposes, we'll just mark it as completed
    if (!completedAssessments.includes(id)) {
      const assessment = assessments.find(a => a.id === id);
      if (assessment) {
        setXp(xp + assessment.xp);
        setCompletedAssessments([...completedAssessments, id]);
      }
    }
  };
  const allCompleted = completedAssessments.length === assessments.length;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} data-id="element-589">
      <div className="mb-8" data-id="element-590">
        <div className="flex justify-between items-center mb-4" data-id="element-591">
          <h1 className="text-2xl font-bold text-white" data-id="element-592">Assessments Hub</h1>
          <div className="bg-[#1C2541] rounded-full py-1 px-4 flex items-center" data-id="element-593">
            <span className="text-[#FFD700]" data-id="element-594">✨</span>
            <span className="ml-2 font-bold" data-id="element-595">{xp} XP</span>
          </div>
        </div>
        <p className="text-[#B8C0E0]" data-id="element-596">
          Complete assessments to unlock your career matches
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-2" data-id="element-597">
        {assessments.map(assessment => {
        const isCompleted = completedAssessments.includes(assessment.id);
        return <motion.div key={assessment.id} whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={() => startAssessment(assessment.id)} data-id="element-598">
              <Card className={`cursor-pointer ${isCompleted ? 'border-[#19D3DA]' : ''}`} data-id="element-599">
                <div className="flex justify-between items-start mb-4" data-id="element-600">
                  <div className="text-3xl" data-id="element-601">{assessment.icon}</div>
                  {isCompleted ? <Badge color="green" data-id="element-602">
                      <CheckCircleIcon size={16} className="mr-1" data-id="element-603" />
                      Completed
                    </Badge> : <Badge color="blue" data-id="element-604">
                      <span className="mr-1" data-id="element-605">+{assessment.xp} XP</span>
                    </Badge>}
                </div>
                <h3 className="text-lg font-semibold mb-1" data-id="element-606">
                  {assessment.title}
                </h3>
                <p className="text-[#B8C0E0] text-sm mb-4" data-id="element-607">
                  {assessment.description}
                </p>
                <div className="flex justify-between items-center" data-id="element-608">
                  <span className="text-sm text-[#B8C0E0]" data-id="element-609">
                    {assessment.time}
                  </span>
                  <button className={`flex items-center text-sm font-medium rounded-full py-1 px-3 ${isCompleted ? 'bg-[#19D3DA]/20 text-[#19D3DA]' : 'bg-[#7D56F4] text-white'}`} data-id="element-610">
                    {isCompleted ? 'Retake' : 'Start'}
                    {!isCompleted && <ArrowRightIcon size={14} className="ml-1" data-id="element-611" />}
                  </button>
                </div>
                {isCompleted && <div className="mt-3 pt-3 border-t border-[#3A506B] flex items-center" data-id="element-612">
                    <div className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] w-6 h-6 rounded-full flex items-center justify-center text-xs" data-id="element-613">
                      🏆
                    </div>
                    <span className="ml-2 text-sm font-medium text-[#FFD700]" data-id="element-614">
                      {assessment.badge} Badge Earned!
                    </span>
                  </div>}
              </Card>
            </motion.div>;
      })}
      </div>
      <div className="mt-8 flex justify-center" data-id="element-615">
        <motion.button whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={onComplete} disabled={!allCompleted} className={`py-3 px-8 rounded-full font-medium flex items-center ${allCompleted ? 'bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white' : 'bg-[#1C2541] text-[#B8C0E0]'}`} data-id="element-616">
          {allCompleted ? <>
              <span data-id="element-617">View Your Career Matches</span>
              <ArrowRightIcon size={18} className="ml-2" data-id="element-618" />
            </> : <>
              <LockIcon size={16} className="mr-2" data-id="element-619" />
              <span data-id="element-620">Complete All Assessments</span>
            </>}
        </motion.button>
      </div>
    </motion.div>;
};